import { useEffect, useRef, useState, createElement } from "react";
import { FloppyDisk, X, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useCms } from "@/context/CmsContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * <Editable id="home.hero.title" as="h1" className="..." defaultHtml="<p>...</p>" />
 *
 * Renders the override HTML from CmsContext if present, otherwise renders `defaultHtml`
 * (falling back to `children` when no defaultHtml).
 *
 * Admin/trainer users can toggle "Edit page" mode (CmsEditToggle). Each <Editable> then
 * gets a dashed orange outline; double-clicking opens an inline rich-text editor that
 * saves back to the backend via PUT /api/cms/content.
 */
export default function Editable({
  id,
  as: Tag = "div",
  className = "",
  defaultHtml,
  children,
  inline = false,
  testid,
}) {
  const { content, editMode, save, revert, registerKey } = useCms();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "trainer");
  const initialHtml = (defaultHtml ?? (typeof children === "string" ? children : null)) || "";

  useEffect(() => { registerKey(id, initialHtml); }, [id, initialHtml, registerKey]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const overrideHtml = content[id];
  const html = overrideHtml ?? initialHtml;
  const hasHtml = (overrideHtml !== undefined) || (defaultHtml !== undefined);

  const openEditor = () => {
    if (!isAdmin || !editMode) return;
    setEditing(true);
    setTimeout(() => {
      if (ref.current) {
        ref.current.innerHTML = html;
        ref.current.focus();
      }
    }, 10);
  };

  const cmd = (c, arg) => { document.execCommand(c, false, arg); ref.current?.focus(); };
  const linkCmd = () => {
    const url = window.prompt("Link URL (https://... or /path):");
    if (url) cmd("createLink", url);
  };

  const handleSave = async () => {
    if (!ref.current) return;
    const newHtml = ref.current.innerHTML.trim();
    if (newHtml === initialHtml.trim() && overrideHtml) {
      try { await revert(id); toast.success("Reverted to default"); }
      catch { toast.error("Revert failed"); }
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await save(id, newHtml);
      toast.success("Saved");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleRevert = async () => {
    if (!window.confirm("Revert this block to the original site text?")) return;
    try { await revert(id); toast.success("Reverted"); setEditing(false); }
    catch { toast.error("Revert failed"); }
  };

  // READ-ONLY DISPLAY
  if (!editing) {
    const showEdit = isAdmin && editMode;
    const wrapClasses = `cms-editable ${showEdit ? "cms-editable--edit-on" : ""} ${className}`;
    const props = {
      className: wrapClasses,
      "data-cms-id": id,
      "data-testid": testid || `cms-${id}`,
      title: showEdit ? "Double-click to edit" : undefined,
      onDoubleClick: showEdit ? openEditor : undefined,
    };
    if (hasHtml) {
      return createElement(Tag, { ...props, dangerouslySetInnerHTML: { __html: html } });
    }
    return createElement(Tag, props, children);
  }

  // EDIT MODE
  return (
    <div className={`cms-editor-wrap ${className}`} data-cms-id={id}>
      <div className="cms-toolbar" data-testid={`cms-toolbar-${id}`}>
        <button type="button" onClick={() => cmd("bold")} title="Bold"><strong>B</strong></button>
        <button type="button" onClick={() => cmd("italic")} title="Italic"><em>I</em></button>
        <button type="button" onClick={() => cmd("underline")} title="Underline"><u>U</u></button>
        {!inline && <>
          <button type="button" onClick={() => cmd("formatBlock", "h2")} title="Heading 2">H2</button>
          <button type="button" onClick={() => cmd("formatBlock", "h3")} title="Heading 3">H3</button>
          <button type="button" onClick={() => cmd("formatBlock", "p")} title="Paragraph">¶</button>
          <button type="button" onClick={() => cmd("insertUnorderedList")} title="Bullet list">• List</button>
        </>}
        <button type="button" onClick={linkCmd} title="Link">🔗</button>
        <button type="button" onClick={() => cmd("removeFormat")} title="Clear formatting">Aa</button>
        <span className="cms-toolbar-spacer" />
        {overrideHtml && (
          <button type="button" className="cms-toolbar-revert" onClick={handleRevert} title="Restore default">
            <ArrowCounterClockwise size={14} weight="bold" /> Default
          </button>
        )}
        <button type="button" className="cms-toolbar-cancel" onClick={() => setEditing(false)} title="Cancel">
          <X size={14} weight="bold" />
        </button>
        <button type="button" className="cms-toolbar-save" disabled={saving} onClick={handleSave} title="Save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "..." : "Save"}
        </button>
      </div>
      <div
        ref={ref}
        className="cms-editor-surface"
        contentEditable
        suppressContentEditableWarning
        data-testid={`cms-editor-${id}`}
      />
      <div className="cms-editor-hint">Editing: <code>{id}</code> — double-click to edit, then Save</div>
    </div>
  );
}
